<template>
    <DCard padding>
        <DRow
            class="mb-3"
            align-horizontal="start"
            align-vertical="end"
        >
            <DCol lg="3">
                <DInput
                    id="search"
                    v-model="searchValue"
                    :label="__('orders.search_order_number')"
                    :is-valid="searchIsValid"
                    :invalid-feedback="searchError"
                />
            </DCol>
            <DCol
                lg="3"
                class="d-flex gap-2"
            >
                <DButton
                    id="filter-apply"
                    :label="__('orders.search')"
                    @click="search"
                />
                <DButton
                    id="filter-reset"
                    icon="filter-off"
                    :label="__('messages.actions.reset_filter')"
                    @click="resetFilters"
                />
            </DCol>
            <DCol
                sm="6"
                class="text-right"
            >
                <DButton
                    id="table-configuration"
                    icon="cog-outline"
                    :label="__('messages.list_configuration')"
                    @click="showListViewModal = true"
                />
            </DCol>
        </DRow>
        <DRow>
            <DCol lg="12">
                <DCard
                    class="orders-box"
                >
                    <template #default>
                        <DTable
                            :fields="getFields"
                            :items="orderCollection.orders.concat(orderCollection.invalidOrders)"
                            :column-filter="filterColumnEnabled"
                            :sorter="isSortable"
                            :sorter-value="sorterValue"
                            @update:sorter-value="updateOrdering"
                        >
                            <template
                                #status-filter
                            >
                                <DMultiSelect
                                    id="status-dropdown"
                                    :options="filterOptions"
                                    selection-type="text"
                                    :selected="selectedStatus"
                                    @update="onFilterChange"
                                />
                            </template>
                            <template #orderNumber="{item}">
                                <td>{{ item.orderNumber }} </td>
                                <td
                                    v-if="!item.valid"
                                    :colspan="colspan"
                                >
                                    <DTag
                                        :label="__('orders.invalid_order')"
                                        color="danger"
                                    /> {{ item.error }}
                                </td>
                            </template>
                            <template #payment="{item}">
                                <td v-if="item.valid">
                                    {{ item.totalPayment }}<br>
                                    <DTag
                                        v-if="item.customer"
                                        :label="'via ' + item.paymentMethod"
                                    />
                                </td>
                            </template>
                            <template #entryDate="{item}">
                                <td v-if="item.valid && item.dateCreated">
                                    {{ setDateFormat(item.dateCreated) }}
                                </td>
                                <td v-if="item.valid && !item.dateCreated" />
                            </template>
                            <template #status="{item}">
                                <td v-if="item.valid && item.status">
                                    <DTag :label="__('orders.woo_commerce.statuses.'+item.status)" />
                                </td>
                                <td v-if="item.valid && !item.status" />
                            </template>
                            <template #customer="{item}">
                                <td
                                    v-if="item.valid && item.customer"
                                    :style="!item.assignedCustomerId && item.databaseCustomers ? 'background-color: #ffff66' : '#ffffff'"
                                >
                                    <div v-if="item.customer.company">
                                        {{ item.customer.company }}
                                    </div>
                                    <div v-if="item.customer.firstname || item.customer.lastname">
                                        <span v-if="item.customer.firstname">{{ item.customer.firstname }}</span>
                                        <span v-if="item.customer.lastname">{{ item.customer.lastname }}</span>
                                    </div>
                                    <Link
                                        v-if="item.databaseCustomers && item.assignedCustomerId"
                                        :href="$route('customers.customers.show', {customer: item.assignedCustomerId})"
                                    >
                                        {{ item.assignedCustomerId }}
                                    </Link>
                                    <p v-if="item.customer.id && item.customer.status !== 'active'">
                                        <DTag
                                            :label="__('orders.inactive')"
                                            color="danger"
                                        />
                                    </p>
                                </td>
                                <td v-if="item.valid && !item.customer" />
                            </template>
                            <template #products="{item}">
                                <td v-if="item.valid">
                                    <p
                                        v-for="(product, productIndex) in item.items"
                                        :key="productIndex"
                                    >
                                        - <strong>{{ product.domainName ?? product.name }}</strong>
                                    </p>
                                    <!--                                    <p v-if="item.transferId !== undefined && item.transferId !== null">-->
                                    <!--                                        <Link-->
                                    <!--                                            :href="$route('contracts.transfers', {transferId: item.transferId})"-->
                                    <!--                                        >-->
                                    <!--                                            - Contract transfer:  {{ item.transferId }}-->
                                    <!--                                        </Link>-->
                                    <!--                                    </p>-->
                                </td>
                            </template>
                            <template #orderNote="{item}">
                                <td
                                    v-if="item.orderNote"
                                    class="text-break"
                                >
                                    <span
                                        v-if="item.orderNote.note.length > 50"
                                    >
                                        {{ item.orderNote.note.substring(0, 50) }} ...<a
                                            href="javascript:void(0);"
                                            @click="showNote(item)"
                                        >{{ __('orders.more') }}</a>
                                    </span>
                                    <span v-else>
                                        {{ item.orderNote.note }}
                                    </span>
                                </td>
                                <td v-else />
                            </template>
                            <template #actions="{item}">
                                <td style="display: flex; grid-gap: 5px;">
                                    <DButton
                                        mini
                                        icon="eye"
                                        @click="orderDetail(item)"
                                    />
                                    <DDropdown
                                        toggler-text="..."
                                        color="secondary"
                                        :caret="false"
                                    >
                                        <DDropdownItem
                                            v-if="userPermissions.includes('order.reject') && item.status !== 'completed'"
                                            @click="rejectOrder(item, false)"
                                        >
                                            {{ __('orders.reject') }}
                                        </DDropdownItem>
                                        <DDropdownItem
                                            v-if="userPermissions.includes('order.reject') && item.status !== 'completed'"
                                            @click="rejectOrder(item, true)"
                                        >
                                            {{ __('orders.silent_reject') }}
                                        </DDropdownItem>
                                        <span
                                            v-if="itemAcceptable(item) && userPermissions.includes('order.accept')"
                                        >

                                            <DDropdownItem
                                                @click="prepareDataForOrderAccept(item)"
                                            >
                                                {{ __('orders.accept') }}
                                            </DDropdownItem>

                                        </span>
                                        <DDropdownItem
                                            @click="showOrderNoteDialog(item)"
                                        >
                                            {{ __('orders.note.label') }}
                                        </DDropdownItem>
                                    </DDropdown>
                                </td>
                            </template>
                        </DTable>
                        <DPagination
                            :active-page="orderCollection.page"
                            :pages="orderCollection.totalPages"
                            align="center"
                            @update:activePage="loadPage"
                        />
                        <OrderNoteCreateModal
                            v-if="showNoteCreateModal"
                            :show-modal="showNoteCreateModal"
                            :title="`${__('orders.note.title')} ${selectedItem.orderNumber}`"
                            :order-note="orderNoteDialog.currentNote.value"
                            :errors="usePage().props.errors.orderNoteDialog"
                            @close="showNoteCreateModal = false"
                            @confirm="saveOrderNote"
                        />
                        <DModal
                            :show="showNoteModal"
                            :title="`${__('orders.note.title')} ${selectedItem.orderNumber}`"
                            is-big
                            @close="showNoteModal = false"
                        >
                            <div class="p-3">
                                <DFormTextarea
                                    :value="selectedItem.orderNote ? selectedItem.orderNote.note : ''"
                                    :disabled="true"
                                    rows="5"
                                />
                            </div>
                        </DModal>
                        <DModal
                            v-model:show="showAcceptDialog"
                            :title="dialogTitle"
                            :message="dialogMessage"
                            is-confirm
                            @close="showAcceptDialog = false"
                            @confirm="acceptCallback"
                        />
                        <DModal
                            v-model:show="showRejectDialog"
                            :title="dialogTitle"
                            :message="dialogMessage"
                            is-confirm
                            is-error
                            @close="showRejectDialog = false"
                            @confirm="rejectCallback"
                        />
                        <AssignCustomerDialogModal
                            v-if="showAssignCustomerDialog"
                            :show-modal="showAssignCustomerDialog"
                            :selected-item="selectedItem"
                            :database-customers="databaseCustomers"
                            @confirm-modal="assignCustomerAndAccept"
                            @close-modal="showAssignCustomerDialog = false"
                        />
                        <DomainOwnerInfoModal
                            v-if="showDomainsModal"
                            :show-modal="showDomainsModal"
                            :domains="selectedItem.domains"
                            :billing-comments="selectedItem.billingComments"
                            :errors="domainErrors"
                            @confirm="getDomainOwnerDetails"
                            @closeModal="closeDomainsModal"
                        />
                    </template>
                </DCard>
            </DCol>
        </DRow>
        <ListViewModal
            v-if="showListViewModal"
            :show-modal="showListViewModal"
            :user-list-view="userListView"
            @close-modal="showListViewModal = false"
        />
    </DCard>
</template>

<script setup>
import OrderNoteCreateModal from '~@/Components/Orders/OrderNoteCreateModal'
import AssignCustomerDialogModal from '~@/Components/Orders/WooCommerce/AssignCustomerDialogModal.vue'
import { computed, ref } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import { route } from '~@/Composables/route'
import { __ } from '~@/Composables/translation'
import ListViewModal from '~@/Components/ListView/ListViewModal'
import DomainOwnerInfoModal from '~@/Components/Orders/WooCommerce/DomainOwnerInfoModal'

const props = defineProps({
    orderCollection: {
        type: Object,
        default () {
            return {}
        }
    },
    errors: {
        type: Object,
        default () {
            return {}
        }
    },
    page: {
        type: Number,
        default: 1
    },
    orderClient: {
        type: String,
        default: ''
    },
    orderBy: {
        type: String,
        default: 'id'
    },
    orderByAsc: {
        type: Boolean,
        default: false
    },
    userListView: {
        type: Array,
        default: () => {
            return []
        }
    }
})

const params = new URLSearchParams(window.location.search)
const filter = []
for (const param of params.entries()) {
    if (param[0].match(/^statuses[[]\d*[\]]$/)) {
        filter.push(param[1])
    }
}
const colspan = ref(5)
const filterColumnEnabled = ref(true)
getColSpan()
const orderByData = ref(props.orderBy)
const orderByAscData = ref(props.orderByAsc ? 0 : 1)
const showListViewModal = ref(false)
const getFields = computed(() => {
    const availableList = []

    props.userListView.forEach((item) => {
        if (item.show_column === true) {
            availableList.push({
                key: item.column,
                label: __('settings.list_view.' + item.list + '.' + item.column),
                sorter: item.sort_column_enabled,
                _style: item.width !== null ? { minWidth: item.width + 'px', maxWidth: item.width + 'px' } : {},
                filter: item.column === 'status'
            })
        }
        if (item.column === 'status') {
            filterColumnEnabled.value = item.show_column
        }
    })

    getColSpan()

    return availableList
})
const sorterValue = computed(() => {
    return {
        column: orderByData.value,
        asc: !!orderByAscData.value
    }
})
const isSortable = computed(() => {
    if (!sorterValue.value.column) return false

    return { external: true }
})

const searchValue = params.get('search') ? ref(params.get('search')) : ref('')
const selectedStatus = filter.length === 0 && !params.get('search') ? ['processing'] : filter
const selectedItem = ref({})
const orderNoteDialog = ref({
    currentNote: {}
})
const showNoteModal = ref(false)
const showNoteCreateModal = ref(false)
const showAssignCustomerDialog = ref(false)
const databaseCustomers = ref([])
const showAcceptDialog = ref(false)
const showRejectDialog = ref(false)
const dialogTitle = ref('')
const dialogMessage = ref('')
let acceptCallback = () => { return {} }
let rejectCallback = () => { return {} }

const filterOptions = computed(() => {
    return [
        { value: 'pending', text: __('orders.woo_commerce.statuses.pending'), selected: selectedStatus.includes('pending') },
        { value: 'processing', text: __('orders.woo_commerce.statuses.processing'), selected: selectedStatus.includes('processing') },
        { value: 'on-hold', text: __('orders.woo_commerce.statuses.on-hold'), selected: selectedStatus.includes('on-hold') },
        { value: 'completed', text: __('orders.woo_commerce.statuses.completed'), selected: selectedStatus.includes('completed') },
        { value: 'cancelled', text: __('orders.woo_commerce.statuses.cancelled'), selected: selectedStatus.includes('cancelled') },
        { value: 'refunded', text: __('orders.woo_commerce.statuses.refunded'), selected: selectedStatus.includes('refunded') },
        { value: 'failed', text: __('orders.woo_commerce.statuses.failed'), selected: selectedStatus.includes('failed') },
        { value: 'trash', text: __('orders.woo_commerce.statuses.trash'), selected: selectedStatus.includes('trash') }
    ]
})
const searchError = computed(() => {
    return props.errors.search ?? __('orders.search_error')
})
const userPermissions = computed(() => {
    return usePage().props.auth.permissions
})
function loadPage (page) {
    return router.get(route('orders.woocommerce.index', { _query: { page, statuses: selectedStatus, order_by: orderByData.value, order_by_asc: orderByAscData.value ? 0 : 1 } }))
}
function onFilterChange (statuses) {
    return router.get(route('orders.woocommerce.index', { _query: { statuses: statuses, page: props.page, order_by: orderByData.value, order_by_asc: orderByAscData.value ? 0 : 1 } }))
}
function showOrderNoteDialog (order) {
    selectedItem.value = order
    showNoteCreateModal.value = true

    orderNoteDialog.value.currentNote.value = order.orderNote ?? {
        id: null, // new note
        order_id: order.id,
        oder_no: order.orderNumber,
        note: ''
    }
}
function saveOrderNote (item) {
    // This seems to be overly complex but is the result of not being able
    // to trigger a normal form submit when a form is within a modal...
    // as the confirm button of the modal is outside the form
    if (item.id !== null) {
        router.patch(
            route('ordernotes.update', {
                orderClient: props.orderClient,
                orderNote: item.id,
                _query: { page: props.page, statuses: selectedStatus }
            }),
            item,
            {
                errorBag: 'orderNoteDialog',
                onFinish: () => {
                    // Open again if there is any error
                    showNoteCreateModal.value = !!props.errors.orderNoteDialog
                }
            }
        )
    } else {
        router.post(route('ordernotes.create', {
            orderClient: props.orderClient,
            orderId: selectedItem.value.id,
            orderNumber: selectedItem.value.orderNumber,
            _query: { page: props.page, statuses: selectedStatus }
        }), item, {
            errorBag: 'orderNoteDialog',
            onFinish: () => {
                // Open again if there is any error
                showNoteCreateModal.value = !!props.errors.orderNoteDialog
            }
        })
    }
}
function searchIsValid (value) {
    if (value.length === 0) {
        return null
    }
    return value.trim().length > 0
}
function search () {
    if (searchIsValid(searchValue.value) !== true) {
        return
    }

    router.get(route('orders.woocommerce.index', { _query: { search: searchValue.value.trim(), order_by: orderByData.value, order_by_asc: orderByAscData.value ? 0 : 1 } }))
}
function orderDetail (item) {
    router.get(route('orders.details', { order: item.id, client: 'WooCommerce' }))
}
function resetFilters () {
    router.get(route('orders.woocommerce.index', { _query: { page: props.page } }))
}
function showNote (order) {
    selectedItem.value = order
    showNoteModal.value = true
}
function itemAcceptable (order) {
    // TODO Check with Sonja if there are others acceptable statuses
    const acceptableStatuses = ['processing']

    if (acceptableStatuses.includes(order.status) && order.valid) {
        return true
    }
}
function assignCustomerAndAccept (customer) {
    showAssignCustomerDialog.value = false
    customerId.value = customer

    if (selectedItem.value.domains?.length > 0) {
        showDomainsModal.value = true
        return
    }

    acceptOrder()
}

// For WooCommerce, customer assign process is a part of order accept.
// We will show customer assign dialog if there are matching ones and get data from it or create new customer
function prepareDataForOrderAccept (order) {
    selectedItem.value = order

    if (order.databaseCustomers.length > 0) {
        databaseCustomers.value = order.databaseCustomers
        showAssignCustomerDialog.value = true
    } else if (order.domains?.length > 0) {
        showDomainsModal.value = true
    } else {
        const orderNumber = order.orderNumber
        dialogTitle.value = __('orders.accept_order_dialog_title', { orderNumber })
        dialogMessage.value = __('orders.accept_dialog_message', { orderNumber })
        showAcceptDialog.value = true

        acceptCallback = () => {
            acceptOrder()
        }
    }
}

const showDomainsModal = ref(false)

const customerId = ref(null)
const domainsData = ref([])
const domainErrors = ref(null)

// processing -> completed status
function acceptOrder () {
    router.post(
        route('orders.woocommerce.accept-order', {
            order: selectedItem.value.id,
            _query: {
                page: props.page,
                statuses: selectedStatus,
                domains: domainsData.value
            }
        }),
        {
            customer_id: customerId.value
        },
        {
            onSuccess: () => {
                showDomainsModal.value = false
                showAcceptDialog.value = false
            },
            onError: (errors) => {
                domainErrors.value = errors
            }
        }
    )
}

// processing -> cancelled status
function rejectOrder (order, silent) {
    const orderNumber = order.orderNumber
    dialogTitle.value = __('orders.reject_order_dialog_title', { orderNumber })
    dialogMessage.value = __('orders.reject_dialog_message', { orderNumber })
    rejectCallback = () => {
        router.patch(route('orders.woocommerce.reject-order', { order: order.id, _query: { page: props.page, statuses: selectedStatus } }), { silent: silent })
    }
    showRejectDialog.value = true
}

function updateOrdering (event) {
    router.get(route('orders.woocommerce.index', {
        _query: {
            page: props.page,
            search: searchValue.value.trim(),
            statuses: selectedStatus.value,
            order_by: event.column,
            order_by_asc: event.asc ? 0 : 1
        }
    }))
}

function getColSpan () {
    let activeListLength = 0

    props.userListView.forEach((item) => {
        if (item.show_column === true) {
            ++activeListLength
        }
    })

    const colspanData = activeListLength - 5

    if (colspanData < 0) {
        colspan.value = 0

        return
    }

    if (colspanData < 5) {
        colspan.value = colspanData + 1

        return
    }

    colspan.value = colspanData
}

function closeDomainsModal () {
    showDomainsModal.value = false
}

function getDomainOwnerDetails (domains) {
    domainsData.value = Object.values(domains)

    acceptOrder()
}
</script>

<style lang="scss" scoped>
// Forcing Status Column to be bigger despite having no space.
// Doing it manually because CoreUI can't scale a Column if the space is too tight
.orders-box th:nth-child(3) {
    width: 170px;
}
.database-customers {
    align-items: flex-end;
    gap: 10px;
}
.select-margin {
    margin-bottom: -8px;
}
.select-customer-table th:nth-child(1) {
    width: 20% !important;
}
.select-customer-table th:nth-child(2) {
    width: 40% !important;
}
.select-customer-table th:nth-child(3) {
    width: unset !important;
    border-bottom-width: 1px;
}
.different-customer-data {
    background-color: #ffff66;
}
.select-customer-table th {
    border-bottom: none !important;
}
.orders-blocked-text {
    color: var(--colorDanger);
    font-weight: bold;
}
</style>
